import {useState} from 'react'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {AlertTriangle} from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import type {DefinitionChangeImpactResponse} from '@/types/attribute-definition'
import {SqlEditorDialog} from './SqlEditorDialog'

interface DefinitionImpactConfirmDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onCancel?: () => void
    impact: DefinitionChangeImpactResponse | null
    onConfirm: (migrationSql?: string) => void
    isSaving?: boolean
}

const PREVIEW_COUNT = 5

export function DefinitionImpactConfirmDialog({
    open,
    onOpenChange,
    onCancel,
    impact,
    onConfirm,
    isSaving = false,
}: DefinitionImpactConfirmDialogProps) {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const canMigrate = hasPermission(permissions.attributeDefinitions.migrate)
    const [confirmed, setConfirmed] = useState(false)
    const [sqlEditorOpen, setSqlEditorOpen] = useState(false)

    function handleOpenChange(newOpen: boolean) {
        if (!newOpen) {
            setConfirmed(false)
        }
        onOpenChange(newOpen)
    }

    function handleCancel() {
        setConfirmed(false)
        onOpenChange(false)
        onCancel?.()
    }

    function handleSaveWithMigration() {
        onOpenChange(false)
        onConfirm()
    }

    function handleSqlApply(sql: string) {
        setSqlEditorOpen(false)
        onConfirm(sql)
    }

    function handleDirectConfirm() {
        onOpenChange(false)
        onConfirm()
    }

    if (!impact) return null

    const previewEntities = impact.affectedEntities.slice(0, PREVIEW_COUNT)
    const prefillSql = impact.migrationSqlTemplate ?? ''

    return (
        <>
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent
                    data-testid="definition-impact-confirm-dialog"
                    className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {i18n.attributeDefinitions.impact.warningTitle}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            {i18n.attributeDefinitions.impact.warningTitle}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {impact.affectedValueCount > 0
                                    ? i18n.attributeDefinitions.impact.affectedCount({
                                        valueCount: impact.affectedValueCount,
                                        entityCount: impact.affectedEntityCount,
                                    })
                                    : i18n.attributeDefinitions.impact.affectedEntitiesOnly({
                                        entityCount: impact.affectedEntityCount,
                                    })
                                }
                            </AlertDescription>
                        </Alert>

                        {impact.breakingChanges.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-sm font-medium">{i18n.attributeDefinitions.impact.breakingChanges}</p>
                                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                    {impact.breakingChanges.map((change, idx) => (
                                        <li key={idx}>{change.message}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {previewEntities.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium">{i18n.attributeDefinitions.impact.affectedEntities}</p>
                                <div className="rounded-md border">
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {previewEntities.map(entity => (
                                                <tr key={entity.entityId} className="border-b last:border-b-0">
                                                    <td className="px-3 py-2">{entity.displayName}</td>
                                                    <td className="px-3 py-2 text-muted-foreground">
                                                        {i18n.attributeDefinitions.impact.currentValue({value: entity.currentValue})}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {impact.affectedEntityCount > PREVIEW_COUNT && (
                                    <p className="text-xs text-muted-foreground">
                                        {i18n.attributeDefinitions.impact.andMore({count: impact.affectedEntityCount - PREVIEW_COUNT})}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="flex items-start gap-2 pt-2">
                            <Checkbox
                                id="impact-confirm-checkbox"
                                checked={confirmed}
                                onCheckedChange={(checked) => setConfirmed(checked === true)}
                                data-testid="impact-confirmation-checkbox"
                            />
                            <label htmlFor="impact-confirm-checkbox" className="text-sm cursor-pointer">
                                {impact.affectedValueCount > 0
                                    ? i18n.attributeDefinitions.impact.confirmationLabel({
                                        valueCount: impact.affectedValueCount,
                                        entityCount: impact.affectedEntityCount,
                                    })
                                    : i18n.attributeDefinitions.impact.confirmationLabelEntitiesOnly({
                                        entityCount: impact.affectedEntityCount,
                                    })
                                }
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={handleCancel} data-testid="impact-confirm-cancel">
                                {i18n.common.cancel}
                            </Button>
                            {canMigrate && impact.migrationRequired ? (
                                <Button
                                    onClick={handleSaveWithMigration}
                                    disabled={!confirmed || isSaving}
                                    data-testid="save-with-migration-button"
                                >
                                    {isSaving && <ButtonSpinner/>}
                                    {i18n.attributeDefinitions.impact.saveWithMigration}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleDirectConfirm}
                                    disabled={!confirmed || isSaving}
                                    data-testid="impact-confirm-button"
                                >
                                    {isSaving && <ButtonSpinner/>}
                                    {i18n.common.confirm}
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <SqlEditorDialog
                open={sqlEditorOpen}
                onOpenChange={(open) => { setSqlEditorOpen(open) }}
                sql={prefillSql}
                onApply={handleSqlApply}
                applyLabel={i18n.attributeDefinitions.migration.migrate}
                showAssist={canMigrate}
                dryRun
            />
        </>
    )
}
